import type { PaletteMode, PaletteOptions, SimplePaletteColorOptions } from "@mui/material"
import { alpha } from "@mui/material/styles"

import palette from "./palette"
import type { CustomShadowOptions } from "./theme-augmentations"

const themeColor = palette("light") as PaletteOptions

const LIGHT_MODE = themeColor.grey?.[500] || "#919EAB"

const DARK_MODE = themeColor.common?.black || "#000"

function createShadow(color: string): CustomShadowOptions {
  const transparent = alpha(color, 0.16)
  const transparent1 = alpha(color, 0.2)
  const transparent2 = alpha(color, 0.14)
  const transparent3 = alpha(color, 0.12)

  const themeColor = palette("light")! as PaletteOptions

  const primary = themeColor.primary as SimplePaletteColorOptions
  const secondary = themeColor.secondary as SimplePaletteColorOptions
  const info = themeColor.info as SimplePaletteColorOptions
  const success = themeColor.success as SimplePaletteColorOptions
  const warning = themeColor.warning as SimplePaletteColorOptions
  const error = themeColor.error as SimplePaletteColorOptions

  return {
    z1: `0 1px 2px 0 ${transparent}`,
    z4: `0 4px 8px 0 ${transparent}, 0 0 0 1px ${transparent}`,
    z8: `0 8px 16px 0 ${transparent}`,
    z12: `0 12px 24px -4px ${transparent}`,
    z16: `0 16px 32px -4px ${transparent}`,
    z20: `0 20px 40px -4px ${transparent}`,
    z24: `0 24px 48px 0 ${transparent}`,

    //
    primary: `0 8px 16px 0 ${alpha(primary.main, 0.24)}`,
    info: `0 8px 16px 0 ${alpha(info.main, 0.24)}`,
    secondary: `0 8px 16px 0 ${alpha(secondary.main, 0.24)}`,
    success: `0 8px 16px 0 ${alpha(success.main, 0.24)}`,
    warning: `0 8px 16px 0 ${alpha(warning.main, 0.24)}`,
    error: `0 8px 16px 0 ${alpha(error.main, 0.24)}`,
    //
    card: `0 0 2px 0 ${alpha(color, 0.2)}, 0 12px 24px -4px ${alpha(color, 0.12)}`,
    dialog: `-40px 40px 80px -8px ${alpha(color, 0.24)}`,
    dropdown: `0 0 2px 0 ${alpha(color, 0.24)}, -20px 20px 40px -4px ${alpha(color, 0.24)}`,
    list: [
      "none",
      `0px 0px 1px -1px ${transparent1},0px 0px 1px 0px ${transparent2},0px 0px 3px 0px ${transparent3}`,
      `0px 0px 1px -2px ${transparent1},0px 0px 2px 0px ${transparent2},0px 0px 5px 0px ${transparent3}`,
      `0px 0px 3px -2px ${transparent1},0px 0px 4px 0px ${transparent2},0px 0px 8px 0px ${transparent3}`,
      `0px 0px 4px -1px ${transparent1},0px 0px 5px 0px ${transparent2},0px 0px 10px 0px ${transparent3}`,
      `0px 0px 5px -1px ${transparent1},0px 0px 8px 0px ${transparent2},0px 0px 14px 0px ${transparent3}`,
      `0px 0px 5px -1px ${transparent1},0px 0px 10px 0px ${transparent2},0px 0px 18px 0px ${transparent3}`,
      `0px 0px 5px -2px ${transparent1},0px 0px 10px 1px ${transparent2},0px 0px 16px 1px ${transparent3}`,
      `0px 0px 5px -3px ${transparent1},0px 0px 10px 1px ${transparent2},0px 0px 14px 2px ${transparent3}`,
      `0px 0px 6px -3px ${transparent1},0px 0px 12px 1px ${transparent2},0px 0px 16px 2px ${transparent3}`,
      `0px 0px 6px -3px ${transparent1},0px 0px 14px 1px ${transparent2},0px 0px 18px 3px ${transparent3}`,
      `0px 0px 7px -4px ${transparent1},0px 0px 15px 1px ${transparent2},0px 0px 20px 3px ${transparent3}`,
      `0px 0px 8px -4px ${transparent1},0px 0px 17px 2px ${transparent2},0px 0px 22px 4px ${transparent3}`,
      `0px 0px 8px -4px ${transparent1},0px 0px 19px 2px ${transparent2},0px 0px 24px 4px ${transparent3}`,
      `0px 0px 9px -4px ${transparent1},0px 0px 21px 2px ${transparent2},0px 0px 26px 4px ${transparent3}`,
      `0px 0px 9px -5px ${transparent1},0px 0px 22px 2px ${transparent2},0px 0px 28px 5px ${transparent3}`,
      `0px 0px 10px -5px ${transparent1},0px 0px 24px 2px ${transparent2},0px 0px 30px 5px ${transparent3}`,
      `0px 0px 11px -5px ${transparent1},0px 0px 26px 2px ${transparent2},0px 0px 32px 5px ${transparent3}`,
      `0px 0px 11px -5px ${transparent1},0px 0px 28px 2px ${transparent2},0px 0px 34px 6px ${transparent3}`,
      `0px 0px 12px -6px ${transparent1},0px 0px 29px 2px ${transparent2},0px 0px 36px 6px ${transparent3}`,
      `0px 0px 13px -6px ${transparent1},0px 0px 31px 3px ${transparent2},0px 0px 38px 7px ${transparent3}`,
      `0px 0px 13px -6px ${transparent1},0px 0px 33px 3px ${transparent2},0px 0px 40px 7px ${transparent3}`,
      `0px 0px 14px -6px ${transparent1},0px 0px 35px 3px ${transparent2},0px 0px 42px 7px ${transparent3}`,
      `0px 0px 14px -7px ${transparent1},0px 0px 36px 3px ${transparent2},0px 0px 44px 8px ${transparent3}`,
      `0px 0px 15px -7px ${transparent1},0px 0px 38px 3px ${transparent2},0px 0px 46px 8px ${transparent3}`,
    ],
  }
}

export default function customShadows(themeMode: PaletteMode) {
  return themeMode === "light" ? createShadow(LIGHT_MODE) : createShadow(DARK_MODE)
}
